import React from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { CalendarIcon } from "lucide-react";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import { DealType } from "@/types/deal";
import { getDealFieldConfig } from "@/config/dealFieldConfig";
import { getCurrencySymbol } from "@/components/ui/currency-select";

interface DynamicDealFieldsProps {
  dealType: DealType;
  formData: any;
  onFieldChange: (field: string, value: string) => void;
  onDateChange: (field: string, date: Date | undefined) => void;
  currency?: string;
}

export const DynamicDealFields: React.FC<DynamicDealFieldsProps> = ({
  dealType,
  formData,
  onFieldChange,
  onDateChange,
  currency = "INR",
}) => {
  const config = getDealFieldConfig(dealType);

  const renderField = (fieldKey: string, fieldConfig: any) => {
    const value = formData[fieldKey] || "";

    switch (fieldConfig.type) {
      case "textarea":
        return (
          <div className="space-y-2" key={fieldKey}>
            <Label htmlFor={fieldKey}>
              {fieldConfig.label}
              {fieldConfig.required && " *"}
            </Label>
            <Textarea
              id={fieldKey}
              placeholder={fieldConfig.placeholder}
              value={value}
              onChange={(e) => onFieldChange(fieldKey, e.target.value)}
              rows={3}
              required={fieldConfig.required}
            />
            {fieldConfig.description && (
              <p className="text-xs text-muted-foreground">
                {fieldConfig.description}
              </p>
            )}
          </div>
        );

      case "select":
        return (
          <div className="space-y-2" key={fieldKey}>
            <Label htmlFor={fieldKey}>
              {fieldConfig.label}
              {fieldConfig.required && " *"}
            </Label>
            <Select
              value={value}
              onValueChange={(selectedValue) =>
                onFieldChange(fieldKey, selectedValue)
              }
              required={fieldConfig.required}
            >
              <SelectTrigger>
                <SelectValue placeholder={fieldConfig.placeholder} />
              </SelectTrigger>
              <SelectContent>
                {fieldConfig.options?.map((option: any) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {fieldConfig.description && (
              <p className="text-xs text-muted-foreground">
                {fieldConfig.description}
              </p>
            )}
          </div>
        );

      case "date":
        const dateValue = value ? new Date(value) : undefined;
        return (
          <div className="space-y-2" key={fieldKey}>
            <Label htmlFor={fieldKey}>
              {fieldConfig.label}
              {fieldConfig.required && " *"}
            </Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn(
                    "w-full justify-start text-left font-normal",
                    !dateValue && "text-muted-foreground"
                  )}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {dateValue ? (
                    format(dateValue, "PPP")
                  ) : (
                    <span>{fieldConfig.placeholder}</span>
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0">
                <Calendar
                  mode="single"
                  selected={dateValue}
                  onSelect={(date) => onDateChange(fieldKey, date)}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
            {fieldConfig.description && (
              <p className="text-xs text-muted-foreground">
                {fieldConfig.description}
              </p>
            )}
          </div>
        );

      case "number":
        return (
          <div className="space-y-2" key={fieldKey}>
            <Label htmlFor={fieldKey}>
              {fieldConfig.label}
              {fieldConfig.required && " *"}
            </Label>
            {fieldConfig.showCurrencyPrefix ? (
              <div className="relative">
                <div className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground font-medium text-sm">
                  {getCurrencySymbol(currency)}
                </div>
                <Input
                  id={fieldKey}
                  type="number"
                  placeholder={fieldConfig.placeholder}
                  value={value}
                  onChange={(e) => onFieldChange(fieldKey, e.target.value)}
                  required={fieldConfig.required}
                  className="pl-12"
                />
              </div>
            ) : (
              <Input
                id={fieldKey}
                type="number"
                placeholder={fieldConfig.placeholder}
                value={value}
                onChange={(e) => onFieldChange(fieldKey, e.target.value)}
                required={fieldConfig.required}
              />
            )}
            {fieldConfig.description && (
              <p className="text-xs text-muted-foreground">
                {fieldConfig.description}
              </p>
            )}
          </div>
        );

      case "text":
      default:
        return (
          <div className="space-y-2" key={fieldKey}>
            <Label htmlFor={fieldKey}>
              {fieldConfig.label}
              {fieldConfig.required && " *"}
            </Label>
            <Input
              id={fieldKey}
              placeholder={fieldConfig.placeholder}
              value={value}
              onChange={(e) => onFieldChange(fieldKey, e.target.value)}
              required={fieldConfig.required}
            />
            {fieldConfig.description && (
              <p className="text-xs text-muted-foreground">
                {fieldConfig.description}
              </p>
            )}
          </div>
        );
    }
  };

  const specificFields = Object.entries(config.specific);

  if (specificFields.length === 0) {
    return null;
  }

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {specificFields.map(([fieldKey, fieldConfig]) =>
          renderField(fieldKey, fieldConfig)
        )}
      </div>
    </div>
  );
};
